// Put common functions for Foxit library in here, so that it's not referenced everywhere,
// and it will allow it to be easier to use and more reusable.
// Don't statically import or export anything other than types here though, or it will cause the whole library to get included,
// which defeats the purpose of lazy-loading this in the first place.

// It is important to note that tree-shaking doesn't work the same for dynamic imports,
// so they should ideally be used to only import exactly what you need.
// So when adding them, you kinda have to analyze exactly everything you're pulling in.
// References: https://github.com/webpack/webpack.js.org/issues/2684
// Also, foxit-pdf-sdk-for-web isn't even tree-shakeable at all in its current state,
// so we could re-evaluate this code if that actually changes at some point.

import {InjectionToken} from '@angular/core';
// eslint-disable-next-line no-restricted-imports
import type {Annot, Markup, PDFViewCtrl, XViewerUI} from '@wrappers/foxit-pdf-sdk-for-web/UIExtension.full';

export type FoxitModule = typeof import('@wrappers/foxit-pdf-sdk-for-web/UIExtension.full');

// this is necessary, to just allow FoxitModule as a constructor parameter
export const FoxitModuleToken = new InjectionToken<FoxitModule>('FoxitModule');

// eslint-disable-next-line no-restricted-imports
export type {
  Annot,
  AnnotationIcon,
  AnnotJson,
  components,
  DeviceRect,
  foxitPrintWindow,
  FragmentComponentOptions,
  FragmentComponentOptions_callback,
  Markup,
  PDFDoc,
  PDFFileOptions,
  PDFPage,
  PDFPageRender,
  PDFRect,
  PDFUI,
  PDFUIOptions,
  PDFViewCtrl,
  PDFViewerOptions,
  STATE_HANDLER_NAMES,
  ThumbnailPanelComponent,
  UIFragmentOptions,
  ViewModes,
} from '@wrappers/foxit-pdf-sdk-for-web/UIExtension.full';

// this is pointing to where the lib folder for foxit will be available at runtime
// (where it's copied to)
export const foxitLibPath = './node_modules_assets/foxit-pdf-sdk-for-web/lib';

export type foxitAddon =
  'edit-graphics' |
  'example' |
  'export-form' |
  'file-property' |
  'form-designer' |
  'full-screen' |
  'h-continuous' |
  'h-facing' |
  'import-form' |
  'multi-media' |
  'password-protect' |
  'path-objects' |
  'print' |
  'recognition-form' |
  'redaction' |
  'text-object' |
  'thumbnail' |
  'undo-redo';

export function getAddonPath(addon: foxitAddon): string {
  return `${foxitLibPath}/uix-addons/${addon}`;
}

// todo: could use /* webpackPrefetch: true */ if necessary
// todo: use webpackExports to only include part of dynamic import
// when we are on webpack 5.x
// https://webpack.js.org/api/module-methods/#magic-comments
export function importFoxit(): Promise<FoxitModule> {
  return import(
    /* webpackChunkName: "foxit-UIExtension-full" */
    '@wrappers/foxit-pdf-sdk-for-web/UIExtension.full'
  );
}

export function isMarkup(value: Annot): value is Markup {
  return value && 'getIntent' in value;
}

// add root container for stuff in foxit that would normally get appendTo to the body,
// but the styling is broken, so we need to appendTo our own container instead.
// This would normally be necessary for everything using styles that are not prefixed with fv,
// because they have to be wrapped to not pollute the rest of our global styles.
// ex: .context-menu-list element added by right-clicking the document for Zoom In/Zoom Out
export function addRootContainerIfNecessary(foxitModule: FoxitModule): [container: HTMLElement, containerViewerUI: XViewerUI] {
  const fullId = `ap-foxit-pdf-web-overlay-container`;
  let container = self.document.getElementById(fullId);
  if (!container) {
    // if it doesn't already exist, then add the element
    container = self.document.createElement('div');
    container.id = fullId;
    container.className = 'ap-foxit-pdf-web-parent';
    self.document.body.appendChild(container);
  }


  return [container, createXViewerUIForRootContainer(foxitModule, container)];
}

// Allow opening contextmenu's in something other than document.body
// search for "Showing a customized context menu" in the docs for overriding these menus
export function createXViewerUIForRootContainer(
  foxitModule: FoxitModule,
  rootContainer: HTMLElement,
): XViewerUI {
  return new (class extends foxitModule.XViewerUI {
    override createContextMenu(
      owner: string | PDFViewCtrl.AnnotComponent,
      anchor: HTMLElement,
      config?: {
        selector?: string;
        appendTo?: HTMLElement;
      },
    ): PDFViewCtrl.viewerui.IContextMenu {
      config = config ?? {};
      config.appendTo = rootContainer;
      return super.createContextMenu(owner, anchor, config);
    }
  })();
}




// This has to be the name of the chunk that has this css file
// It will be bundleName in the "styles" attribute in angular.json
export const foxitCssChunkName = 'foxit-UIExtension';



//
// Previously, this was doing a dynamic import of a css file,
// but since that is webpack specific behavior that was never fully supported,
// it became a build error in ng 14 to do this.
// for reference:
// https://github.com/angular/angular-cli/issues/22358
// https://github.com/angular/angular-cli/issues/23273
// https://github.com/angular/angular-cli/issues/12552
// https://github.com/ag-grid/ag-grid/issues/5381

// Leaving this here for documentation purposes:
// export const foxitCssUniqueId = '@wrappers/foxit-pdf-sdk-for-web/UIExtension.css';
// // todo: could use /* webpackPrefetch: true */ if necessary
// export function importFoxitCss(): Promise<string> {
//   return import(
//     /* webpackChunkName: "foxit-UIExtension-css" */
//     '@wrappers/foxit-pdf-sdk-for-web/UIExtension.css'
//   ).then((x) => x.default);
// }
